/*
 * CONTRACT: McpAdminPage — MCP-Administrations-Seite (RITA-1370)
 *
 * Not-Aus-Toggle:
 * 1. Zeigt AUSSCHLIESSLICH den von Apollo geladenen Server-Wert (fetchPolicy: 'network-only').
 * 2. Feuert NIEMALS beim initialen Render oder durch Query-Data-Aenderungen.
 * 3. AUSSCHLIESSLICH durch expliziten Nutzer-Klick ausgeloest (onChange Handler).
 * 4. Toggle disabled waehrend in-flight setMcpGlobalEnabled-Mutation.
 * 5. isMcpGloballyEnabled wird NIEMALS optimistisch pre-empted — erst nach Server-Erfolg + Refetch.
 *
 * User-Freischaltungs-Toggle:
 * 6. Pro-User-Toggle ruft setMcpEnabled AUSSCHLIESSLICH bei explizitem Klick auf.
 * 7. Toggle disabled waehrend in-flight Mutation fuer diesen User (per-userId tracking).
 * 7b. refetchUsers wird im finally-Block aufgerufen (NICHT nur auf dem happy path) —
 *     auch nach Mutation-Fehler wird der Server-Zustand neu geladen, um Stale-State zu vermeiden.
 *     Schlaegt refetchUsers fehl, wird der Fehler in userErrors[userId] angezeigt (kein stilles Verschlucken).
 *
 * Widerruf:
 * 8. AUSSCHLIESSLICH nach Confirmation-Dialog (McpKeyDialog REVOKE_CONFIRM).
 * 9. Erfolg → McpKeyDialog schliesst Dialog via handleClose(), DANN await onKeyRevoked() hier —
 *    beide Datensaetze werden synchron aktualisiert (kein fire-and-forget).
 *    Schlaegt Refetch fehl, wird revokeRefetchError angezeigt (kein stilles Verschlucken).
 *
 * Governance-Tabelle:
 * 10. Zeigt ALLE Schluessel (inkl. widerrufener) — kein activeKeys-Filter.
 *     Widerrufene Schluessel werden visuell hervorgehoben (Opacity 0.5 + Chip "Widerrufen").
 *
 * Key-Counts:
 * 11. Zeigt '…' waehrend keys-Query laedt und '—' bei Fehler statt falscher 0.
 *
 * Zugriff:
 * 12. Seite erfordert SUPERADMIN — Routes.tsx RequireSuperAdmin leitet Nicht-SUPERADMINs zu /404.
 */
import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Container,
  Divider,
  FormControlLabel,
  IconButton,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useMutation, useQuery } from '@apollo/client';
import isMcpGloballyEnabledQuery from '../../../Apollo/queries/isMcpGloballyEnabled';
import getAllUserQuery from '../../../Apollo/queries/getAllUser';
import getAllMcpKeysQuery from '../../../Apollo/queries/getAllMcpKeys';
import setMcpGlobalEnabledMutation from '../../../Apollo/mutations/setMcpGlobalEnabled';
import setMcpEnabledMutation from '../../../Apollo/mutations/setMcpEnabled';
import McpKeyDialog, { McpKeyDialogMode } from '../../ProfilePage/McpKeyDialog';
import { formatDate, getScopeDisplayName } from '../../ProfilePage/profileMcpUtils';

interface ManagedUser {
  id: string;
  username: string;
  mcp_enabled: boolean | null;
}

// Raw shape returned by getAllUser GraphQL query — id is number (auto-increment from DB).
// McpAdminPage maps id to string via String(u.id) for userId-keyed operations.
// CONTRACT: id ist required (number, kein optional) — erzwingt PrmUser.id-Invariante an Apollo-Grenze.
// CONTRACT: Exportiert fuer Compile-Time-Contract-Tests in user-password-hash-removal.spec.ts.
// Aenderungen (id?: number, id: any) brechen dort die Compile-Time-Assertions.
export interface GetAllUserQueryItem {
  id: number;
  username: string | null;
  mcp_enabled: boolean | null;
}
// CONTRACT: Compile-Time-Sentinel — schlaegt bei tsc fehl wenn GetAllUserQueryItem.id optional ODER any wird.
// 0 extends (1 & T['id']): any-Detektor (1&any=any, 0 extends any=true → never).
//   Faengt: id: any, id?: number. Besteht: id: number (required).
// Exportiert fuer Tests (user-password-hash-removal.spec.ts).
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const MCP_ADMIN_APOLLO_ID_CONTRACT: 0 extends 1 & GetAllUserQueryItem['id']
  ? never
  : GetAllUserQueryItem extends { id: number }
    ? true
    : never = true;

// CONTRACT §11: '…' waehrend keys-Query laedt, '—' bei Fehler, Zahl bei Erfolg
function resolveKeyCount(loading: boolean, error: unknown, count: number | undefined): string | number {
  if (loading) return '…';
  if (error) return '—';
  return count ?? 0;
}

interface ManagedKey {
  id: string;
  key_prefix: string;
  label: string;
  scopes: string[];
  expires_at: string | null;
  revoked_at: string | null;
  last_used_at: string | null;
  createdAt: string;
  userId: string | null;
  username: string | null;
}

function McpAdminPage() {
  // Section 1: Global kill switch
  // CONTRACT §1: fetchPolicy network-only — nie aus Cache
  const {
    data: globalData,
    loading: globalLoading,
    error: globalError,
    refetch: refetchGlobal,
  } = useQuery(isMcpGloballyEnabledQuery, { fetchPolicy: 'network-only' });

  // CONTRACT §4: disabled waehrend in-flight (Mutation + Refetch)
  const [setGlobalEnabled] = useMutation(setMcpGlobalEnabledMutation);
  const [globalBusy, setGlobalBusy] = useState(false);
  const [globalError2, setGlobalError2] = useState<string | null>(null);

  // Section 2: User freischaltung
  // CONTRACT: useQuery typisiert — id ist required (number) erzwingt PrmUser.id-Invariante an Apollo-Grenze.
  // GetAllUserQueryItem ist exportiert — Typ-Aenderung hier bricht Contract-Test.
  const {
    data: usersData,
    loading: usersLoading,
    error: usersError,
    refetch: refetchUsers,
  } = useQuery<{ getAllUser: GetAllUserQueryItem[] }>(getAllUserQuery, { fetchPolicy: 'network-only' });
  // CONTRACT: Wiring-Guard — useQuery fuer getAllUser ist NICHT useQuery<any>.
  // Schlaegt fehl wenn auf useQuery<any> umverdrahtet wird (1&any=any, 0 extends any=true → never).
  // Schlaegt NICHT fehl wenn useQuery<{ getAllUser: GetAllUserQueryItem[] }> (korrekte Typisierung).
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const mcpQueryWiringGuard: 0 extends 1 & typeof usersData ? never : true = true;

  // CONTRACT §7: per-userId in-flight tracking (array, Set-spread incompatible with target es5)
  const [userSavingIds, setUserSavingIds] = useState<string[]>([]);
  const [setUserMcpEnabled] = useMutation(setMcpEnabledMutation);
  const [userErrors, setUserErrors] = useState<Record<string, string>>({});

  // Section 3: Governance keys
  const {
    data: keysData,
    loading: keysLoading,
    error: keysError,
    refetch: refetchKeys,
  } = useQuery(getAllMcpKeysQuery, { fetchPolicy: 'network-only' });

  // Revoke dialog — reuse McpKeyDialog from ProfilePage
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<McpKeyDialogMode>({ type: 'CREATE' });
  // CONTRACT §9: Refetch-Fehler nach Widerruf werden sichtbar angezeigt (kein stilles Verschlucken)
  const [revokeRefetchError, setRevokeRefetchError] = useState<string | null>(null);

  // CONTRACT §11: activeKeyCountByUserId zeigt '...' / '—' wenn keysData noch nicht verfuegbar
  // Berechnung basiert auf ALLEN Schluesseln; nur nicht-widerrufene werden gezaehlt
  const activeKeyCountByUserId = useMemo(() => {
    const allKeys: ManagedKey[] = keysData?.getAllMcpKeys ?? [];
    return allKeys
      .filter((k) => !k.revoked_at)
      .reduce<Record<string, number>>((acc, k) => {
        if (k.userId) {
          acc[k.userId] = (acc[k.userId] ?? 0) + 1;
        }
        return acc;
      }, {});
  }, [keysData]);

  const users: ManagedUser[] = (usersData?.getAllUser ?? []).map((u) => ({
    id: String(u.id),
    username: u.username ?? '—',
    mcp_enabled: u.mcp_enabled ?? false,
  }));

  // CONTRACT §10: ALLE Schluessel anzeigen (inkl. widerrufener)
  const allKeys: ManagedKey[] = keysData?.getAllMcpKeys ?? [];

  // CONTRACT §2+3: onChange ONLY from user click — never on render
  const handleGlobalToggle = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const enabled = event.target.checked;
    setGlobalError2(null);
    // CONTRACT §4: disabled covers BOTH mutation and subsequent refetch
    setGlobalBusy(true);
    try {
      await setGlobalEnabled({ variables: { enabled } });
      await refetchGlobal();
    } catch (err: unknown) {
      setGlobalError2(err instanceof Error ? err.message : 'Fehler beim Speichern');
      await refetchGlobal();
    } finally {
      setGlobalBusy(false);
    }
  };

  // CONTRACT §6+7+7b+7c: per-user toggle, disabled during in-flight, refetch in finally, error sichtbar
  const handleUserToggle = async (userId: string, enabled: boolean) => {
    setUserErrors((prev) => {
      const next = { ...prev };
      delete next[userId];
      return next;
    });
    setUserSavingIds((prev) => [...prev, userId]);
    try {
      await setUserMcpEnabled({ variables: { userId, enabled } });
    } catch (err: unknown) {
      setUserErrors((prev) => ({
        ...prev,
        [userId]: err instanceof Error ? err.message : 'Fehler',
      }));
    } finally {
      // CONTRACT §7b: Immer refetch — auch nach Mutation-Fehler — Server-Zustand ist massgeblich
      // Schlaegt Refetch fehl → Fehler sichtbar anzeigen, kein stilles Verschlucken
      try {
        await refetchUsers();
      } catch {
        setUserErrors((prev) => ({
          ...prev,
          [userId]: 'Daten konnten nicht aktualisiert werden — bitte Seite neu laden.',
        }));
      }
      setUserSavingIds((prev) => prev.filter((id) => id !== userId));
    }
  };

  // CONTRACT §8: only via dialog
  const handleOpenRevoke = (key: ManagedKey) => {
    setRevokeRefetchError(null);
    setDialogMode({ type: 'REVOKE_CONFIRM', id: key.id, label: key.label });
    setDialogOpen(true);
  };

  // CONTRACT §9: Dialog schliesst sich via McpKeyDialog.handleRevoke→handleClose BEVOR diese Funktion
  // aufgerufen wird. Hier nur Refetch — kein setDialogOpen(false) noetig.
  // Schlaegt Refetch fehl → revokeRefetchError setzen, kein stilles Verschlucken.
  const handleKeyRevoked = async (): Promise<void> => {
    try {
      await Promise.all([refetchKeys(), refetchUsers()]);
    } catch {
      setRevokeRefetchError('Schlüsseldaten konnten nicht aktualisiert werden — bitte Seite neu laden.');
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom data-testid="mcp-admin-heading">
        MCP Administration
      </Typography>

      {/* Section 1: Global kill switch */}
      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Globaler Not-Aus
        </Typography>
        <Divider sx={{ mb: 2 }} />

        {globalLoading && <CircularProgress size={20} />}
        {globalError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Status konnte nicht geladen werden: {globalError.message}
          </Alert>
        )}
        {globalError2 && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {globalError2}
          </Alert>
        )}

        {!globalLoading && !globalError && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  // CONTRACT §1: AUSSCHLIESSLICH von Apollo befuellt, NIE lokal vorausoptimiert
                  checked={globalData?.isMcpGloballyEnabled ?? false}
                  // CONTRACT §4: disabled during in-flight (mutation + refetch)
                  disabled={globalBusy}
                  // CONTRACT §2+3: onChange nur durch expliziten Klick — nie durch Render
                  onChange={handleGlobalToggle}
                  color="primary"
                  inputProps={{ 'data-testid': 'mcp-global-toggle' } as React.InputHTMLAttributes<HTMLInputElement>}
                />
              }
              label={globalData?.isMcpGloballyEnabled ? 'MCP global aktiviert' : 'MCP global deaktiviert (Not-Aus)'}
            />
            {globalBusy && <CircularProgress size={16} />}
          </Box>
        )}
      </Paper>

      {/* Section 2: User freischaltung */}
      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Nutzer-Freischaltung
        </Typography>
        <Divider sx={{ mb: 2 }} />

        {usersLoading && <CircularProgress size={20} />}
        {usersError && <Alert severity="error">Benutzer konnten nicht geladen werden: {usersError.message}</Alert>}

        {!usersLoading && users.length > 0 && (
          <Table size="small" data-testid="mcp-user-table">
            <TableHead>
              <TableRow>
                <TableCell>Nutzer</TableCell>
                <TableCell>MCP erlaubt</TableCell>
                <TableCell>Aktive Keys</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.id} data-testid={`mcp-user-row-${user.id}`}>
                  <TableCell>{user.username}</TableCell>
                  <TableCell>
                    {userErrors[user.id] && (
                      // CONTRACT §7c: Fehler-Text MUSS sichtbar angezeigt werden nach Mutations-Fehler
                      <Typography
                        variant="caption"
                        color="error"
                        sx={{ display: 'block' }}
                        data-testid={`mcp-user-error-${user.id}`}
                      >
                        {userErrors[user.id]}
                      </Typography>
                    )}
                    <Switch
                      size="small"
                      // CONTRACT §6: AUSSCHLIESSLICH von Apollo befuellt
                      checked={user.mcp_enabled ?? false}
                      // CONTRACT §7: disabled during in-flight for this user
                      disabled={userSavingIds.includes(user.id)}
                      onChange={(e) => handleUserToggle(user.id, e.target.checked)}
                      inputProps={
                        {
                          'data-testid': `mcp-user-toggle-${user.id}`,
                        } as React.InputHTMLAttributes<HTMLInputElement>
                      }
                    />
                  </TableCell>
                  {/* CONTRACT §11: '…' bei laufender Query, '—' bei Fehler, Zahl bei Erfolg */}
                  <TableCell data-testid={`mcp-user-key-count-${user.id}`}>
                    {resolveKeyCount(keysLoading, keysError, activeKeyCountByUserId[user.id])}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {!usersLoading && !usersError && users.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            Keine Benutzer vorhanden.
          </Typography>
        )}
      </Paper>

      {/* Section 3: Governance keys table — CONTRACT §10: ALLE Schluessel inkl. widerrufener */}
      <Paper variant="outlined" sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Alle Schlüssel
        </Typography>
        <Divider sx={{ mb: 2 }} />
        {/* CONTRACT §9: Refetch-Fehler nach Widerruf sichtbar anzeigen */}
        {revokeRefetchError && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {revokeRefetchError}
          </Alert>
        )}

        {keysLoading && <CircularProgress size={20} />}
        {keysError && <Alert severity="error">Schlüssel konnten nicht geladen werden: {keysError.message}</Alert>}

        {!keysLoading && !keysError && allKeys.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            Keine Schlüssel vorhanden.
          </Typography>
        )}

        {!keysLoading && !keysError && allKeys.length > 0 && (
          <Table size="small" data-testid="mcp-governance-table">
            <TableHead>
              <TableRow>
                <TableCell>Nutzer</TableCell>
                <TableCell>Label</TableCell>
                <TableCell>Präfix</TableCell>
                <TableCell>Rechte</TableCell>
                <TableCell>Ablauf</TableCell>
                <TableCell>Zuletzt</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Aktion</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {allKeys.map((key) => (
                <TableRow
                  key={key.id}
                  data-testid={`mcp-key-row-${key.id}`}
                  sx={key.revoked_at ? { opacity: 0.5 } : undefined}
                >
                  <TableCell>{key.username ?? '—'}</TableCell>
                  <TableCell>{key.label}</TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {key.key_prefix}...
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {key.scopes.map((scope) => (
                        <Tooltip key={scope} title={scope}>
                          <Chip label={getScopeDisplayName(scope)} size="small" />
                        </Tooltip>
                      ))}
                    </Box>
                  </TableCell>
                  <TableCell>{formatDate(key.expires_at)}</TableCell>
                  <TableCell>{formatDate(key.last_used_at)}</TableCell>
                  <TableCell>
                    {key.revoked_at ? (
                      <Chip label="Widerrufen" size="small" color="error" variant="outlined" />
                    ) : (
                      <Chip label="Aktiv" size="small" color="success" variant="outlined" />
                    )}
                  </TableCell>
                  <TableCell>
                    {/* CONTRACT §8: Widerruf nur via Confirmation-Dialog; nur fuer aktive Schluessel */}
                    {!key.revoked_at && (
                      <Tooltip title="Schlüssel widerrufen">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleOpenRevoke(key)}
                          data-testid={`revoke-key-button-${key.id}`}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>

      {/* Revoke confirmation dialog — reuse from ProfilePage */}
      {dialogOpen && (
        <McpKeyDialog
          open={dialogOpen}
          mode={dialogMode}
          onClose={handleDialogClose}
          onKeyCreated={() => {}}
          onKeyRevoked={handleKeyRevoked}
        />
      )}
    </Container>
  );
}

export default McpAdminPage;
